/**
 * INSTALL PROMPT — przycisk instalacji PWA
 * Konwencja: Sikorski (H = si naturalne, B = si bemol)
 *
 * Wyświetla prompt instalacji na iOS i Android
 */

import { useEffect, useState } from 'react'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [isVisible, setIsVisible] = useState(false)
  const [isIOS, setIsIOS] = useState(false)
  const [isInstalled, setIsInstalled] = useState(false)

  useEffect(() => {
    // Sprawdź czy aplikacja jest już zainstalowana
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true)
      return
    }

    // Sprawdź czy to iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent)
    setIsIOS(isIOSDevice)

    // Listen for beforeinstallprompt event (Chrome/Edge/Android)
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
      setIsVisible(true)
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)

    // Check if user dismissed the prompt previously
    const dismissed = localStorage.getItem('pwa-install-dismissed')
    if (dismissed && !isIOSDevice) {
      const dismissedTime = parseInt(dismissed, 10)
      // Show again after 7 days
      if (Date.now() - dismissedTime < 7 * 24 * 60 * 60 * 1000) {
        setIsVisible(false)
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    }
  }, [])

  const handleInstall = async () => {
    if (!deferredPrompt) return

    deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice

    if (outcome === 'accepted') {
      setIsInstalled(true)
    } else {
      localStorage.setItem('pwa-install-dismissed', Date.now().toString())
    }

    setDeferredPrompt(null)
    setIsVisible(false)
  }

  const handleDismiss = () => {
    localStorage.setItem('pwa-install-dismissed', Date.now().toString())
    setIsVisible(false)
  }

  if (isInstalled || !isVisible) return null

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-80 z-50">
      <div className="manuscript-card-elevated p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display text-lg text-ink">Zainstaluj aplikację</h3>
            <p className="font-body text-sm text-ink/70 mt-1">
              Dodaj <strong>Interwały</strong> do ekranu głównego dla szybszego dostępu.
            </p>
            
            {isIOS && (
              <p className="font-ui text-xs text-ink/70 mt-2">
                Stuknij <span className="inline-block px-1 bg-ink/10 rounded">⎋</span> a następnie &quot;Dodaj do ekranu głównego&quot;
              </p>
            )}
          </div>
          
          <button
            onClick={handleDismiss}
            aria-label="Zamknij sugestię instalacji"
            className="font-ui text-xs text-ink/70 hover:text-ink"
          >
            ✕
          </button>
        </div>
        
        {!isIOS && (
          <button
            onClick={handleInstall}
            className="btn-primary w-full mt-3"
          >
            Zainstaluj
          </button>
        )}
      </div>
    </div>
  )
}
